import type React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';

import DynamicBanner, { type BannerCardData } from 'app/ui/components/atoms/dynamic-banner';

type Props = {
  banners: BannerCardData[];
  onBannerClick: (banner: BannerCardData, index: number) => () => void;
};

const AUTOSCROLL_INTERVAL_MS = 7000;

const RailContainer = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing(12)};
`;

const BannerScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  gap: ${({ theme }) => theme.spacing(4)};
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: -${({ theme }) => theme.spacing(5)};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(2)};
  z-index: 10;

  ${({ theme: { spacing, media } }) => `
    ${media.medium(
      () => `
        bottom: ${spacing(3)};
      `,
    )}
  `}
`;

const Dot = styled.button<{ isActive: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background-color: ${({ isActive, theme }) => (isActive ? theme.colors.white : theme.colors.devilsGray)};
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${({ isActive, theme }) => (isActive ? theme.colors.white : theme.colors.devilsGray)};
  }

  ${({ theme: { media } }) => `
    ${media.medium(
      () => `
        width: 10px;
        height: 10px;
      `,
    )}
  `}
`;

const BannerRail: React.FC<Props> = ({ banners, onBannerClick }) => {
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const autoScrollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeBannerIndexRef = useRef(0);

  useEffect(() => {
    activeBannerIndexRef.current = activeBannerIndex;
  }, [activeBannerIndex]);

  const scrollToBanner = useCallback((index: number) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const bannerElements = container.children;

      if (bannerElements[index]) {
        const targetBanner = bannerElements[index] as HTMLElement;

        container.scrollTo({
          left: targetBanner.offsetLeft,
          behavior: 'smooth',
        });

        setActiveBannerIndex(index);
      }
    }
  }, []);

  const startAutoScroll = useCallback(() => {
    if (autoScrollTimerRef.current) {
      clearInterval(autoScrollTimerRef.current);
    }
    if (banners.length <= 1) {
      return;
    }

    autoScrollTimerRef.current = setInterval(() => {
      const nextIndex = (activeBannerIndexRef.current + 1) % banners.length;
      scrollToBanner(nextIndex);
    }, AUTOSCROLL_INTERVAL_MS);
  }, [banners.length, scrollToBanner]);

  useEffect(() => {
    startAutoScroll();
    return () => {
      if (autoScrollTimerRef.current) {
        clearInterval(autoScrollTimerRef.current);
      }
    };
  }, [startAutoScroll]);

  const handleDotClick = (index: number) => {
    scrollToBanner(index);
    startAutoScroll();
  };

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const scrollLeft = container.scrollLeft;
      const containerWidth = container.clientWidth;
      const bannerElements = container.children;

      // Find which banner is most visible in the viewport
      let currentIndex = 0;
      for (let i = 0; i < bannerElements.length; i++) {
        const banner = bannerElements[i] as HTMLElement;
        const bannerLeft = banner.offsetLeft;

        // Check if banner is in view
        if (bannerLeft <= scrollLeft + containerWidth * 0.5) {
          currentIndex = i;
        } else {
          break;
        }
      }

      if (currentIndex !== activeBannerIndex) {
        setActiveBannerIndex(currentIndex);
      }
    }
  }, [activeBannerIndex]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  if (!banners || banners.length === 0) {
    return null;
  }

  return (
    <RailContainer>
      <BannerScrollContainer ref={scrollContainerRef}>
        {banners.map((banner, index) => (
          <DynamicBanner
            key={banner.id}
            banner={banner}
            isSingle={banners.length === 1}
            onClick={onBannerClick(banner, index)}
          />
        ))}
      </BannerScrollContainer>

      {banners.length > 1 && (
        <DotsContainer>
          {banners.map((banner, index) => (
            <Dot
              key={banner.id}
              isActive={index === activeBannerIndex}
              onClick={() => handleDotClick(index)}
              aria-label={`Go to banner ${index + 1}`}
            />
          ))}
        </DotsContainer>
      )}
    </RailContainer>
  );
};

export default BannerRail;
